import React, { useMemo } from "react";
import { LIBRARY_BASE_URL } from "../../props";
import { any, filter, includes, isEmpty, pipe, prop, propEq } from "ramda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";
import { useCurrentOrgId } from "../../auth/authUtils";
import { notBlank } from "../../util/ext";
import { useSelector } from "react-redux";

import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";

const buildMediaPath = media => {
  const id = media?.internalCode ?? media.id;
  switch (media.mediaType) {
    case "module":
      return media.status === "INACTIVE"
        ? `/preview/module/${id}`
        : `/module/${id}`;

    case "anthology":
      return `/anthology/${id}`;

    case "collection": {
      if (notBlank(media.anthology)) {
        const anthologyId = media.anthology?.internalCode ?? media.anthology.id;
        return `/anthology-collection/${anthologyId}/${id}`;
      }
      return `/collection/${id}`;
    }

    default:
      return "";
  }
};

const getBiblioboardUrl = (media, token, orgId) => {
  const baseUrl = new URL(LIBRARY_BASE_URL);
  const path = buildMediaPath(media);
  const url = new URL(path, baseUrl);

  if (media.mediaType === "module" && media.status === "INACTIVE") {
    // automatically sign in user
    if (token) url.searchParams.set("token", token);
    // place user in org they're working from in Creator
    if (orgId && orgId !== "admin") url.searchParams.set("authScope", orgId);
  }

  return url.toString();
};

const shouldShowUrl = (media, currentOrgId) => {
  /**
   * ensure parent modules of anthology or collection have MAC permissions
   * scoped to the org the user is in currently
   */
  const getModulesScopedByOrgId = filter(
    pipe(prop("accessPermissions"), any(propEq("principal", currentOrgId))),
  );

  switch (media.mediaType) {
    case "module":
      return includes(media.status, ["ACTIVE", "INACTIVE"]);

    case "anthology": {
      const scopedParentModules = getModulesScopedByOrgId(media.parentModules);
      return any(isActiveModule, scopedParentModules);
    }

    case "collection": {
      if (notBlank(media.anthology)) {
        const scopedModules = getModulesScopedByOrgId(
          media.anthology.parentModules,
        );
        return any(isActiveModule, scopedModules);
      } else if (!isEmpty(media.parentModules)) {
        const scopedModules = getModulesScopedByOrgId(media.parentModules);
        return any(isActiveModule, scopedModules);
      } else {
        // for unorganized collections
        return false;
      }
    }

    default:
      return false;
  }
};

const isActiveModule = propEq("status", "ACTIVE");

const useBiblioboardUrl = media => {
  const currentOrgId = useCurrentOrgId();
  const token = useSelector(state => state.auth?.user?.token);

  const showUrl = useMemo(() => shouldShowUrl(media, currentOrgId), [
    media,
    currentOrgId,
  ]);

  const url = useMemo(() => getBiblioboardUrl(media, token, currentOrgId), [
    currentOrgId,
    media,
    token,
  ]);

  return [url, showUrl];
};

const BiblioboardLink = ({ media }) => {
  const [url, showUrl] = useBiblioboardUrl(media);
  const action = media?.status === "INACTIVE" ? "Preview" : "View";
  const title = `${action} on BiblioBoard`;

  return (
    showUrl && (
      <Tooltip title={title}>
        <IconButton component={Link} href={url} target="_blank" size="large">
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        </IconButton>
      </Tooltip>
    )
  );
};

export { BiblioboardLink, useBiblioboardUrl };
